import { supabase } from "../supabase-client";
import type { Database } from "../types/database";
import { getClientDocumentCount } from "./client_documents";

export type ClientProfile =
  Database["public"]["Tables"]["client_profiles"]["Row"];
export type ClientProfileInsert =
  Database["public"]["Tables"]["client_profiles"]["Insert"];

/** Normaliza client_profiles para UI: profiles puede venir de join o de columnas directas (lawyer-created) */
export function withProfiles<T extends { full_name?: string | null; email?: string | null; country?: string | null; profiles?: { full_name?: string; email?: string; country?: string; profile_image_url?: string | null } | null }>(
  client: T
): T & { profiles: { full_name: string; email: string; country: string; profile_image_url: string | null } } {
  const p = client.profiles;
  return {
    ...client,
    profiles: p
      ? { full_name: p.full_name ?? "", email: p.email ?? "", country: p.country ?? "", profile_image_url: p.profile_image_url ?? null }
      : { full_name: client.full_name ?? "", email: client.email ?? "", country: client.country ?? "", profile_image_url: null },
  };
}
export type ClientProfileUpdate =
  Database["public"]["Tables"]["client_profiles"]["Update"];

/**
 * Get client profile by user ID
 */
export async function getClientProfile(userId: string) {
  const { data, error } = await supabase
    .from("client_profiles")
    .select("*")
    .eq("id", userId)
    .single();

  if (error) throw error;
  return data ? withProfiles(data) : null;
}

/**
 * Get complete client profile with user data
 */
export async function getCompleteClientProfile(userId: string) {
  const { data, error } = await supabase
    .from("client_profiles")
    .select("*")
    .eq("id", userId)
    .single();

  if (error) throw error;
  return data ? withProfiles(data) : null;
}

/**
 * Get client profile with assigned lawyer
 */
export async function getClientWithLawyer(userId: string) {
  const { data, error } = await supabase
    .from("client_profiles")
    .select("*, lawyer:lawyer_profiles!assigned_lawyer_id(id, bar_number, university, years_of_experience, practice_areas, hourly_rate, phone, profiles(full_name, email, profile_image_url))")
    .eq("id", userId)
    .single();

  if (error) throw error;
  return data ? withProfiles(data) : null;
}

/**
 * Create or update client profile
 */
export async function upsertClientProfile(
  userId: string,
  profile: Omit<ClientProfileInsert, "id">
) {
  const { data, error } = await supabase
    .from("client_profiles")
    .upsert({
      id: userId,
      ...profile,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Update client profile
 */
export async function updateClientProfile(
  userId: string,
  updates: ClientProfileUpdate
) {
  const { data, error } = await supabase
    .from("client_profiles")
    .update(updates)
    .eq("id", userId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Assign lawyer to client
 */
export async function assignLawyer(clientId: string, lawyerId: string) {
  const { data, error } = await supabase
    .from("client_profiles")
    .update({
      assigned_lawyer_id: lawyerId,
      lawyer_connection_date: new Date().toISOString(),
    })
    .eq("id", clientId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Remove assigned lawyer
 */
export async function removeAssignedLawyer(clientId: string) {
  const { data, error } = await supabase
    .from("client_profiles")
    .update({
      assigned_lawyer_id: null,
      lawyer_connection_date: null,
    })
    .eq("id", clientId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Get clients by assigned lawyer ID (from client_profiles table)
 */
export async function getClientsByAssignedLawyer(lawyerId: string) {
  const { data, error } = await supabase
    .from("client_profiles")
    .select("*")
    .eq("assigned_lawyer_id", lawyerId)
    .order("lawyer_connection_date", { ascending: false });

  if (error) throw error;

  const normalized = (data || []).map((c) => withProfiles(c));

  // Get counts for all clients in parallel
  const clientsWithCounts = await Promise.all(
    normalized.map(async (client: { id: string }) => {
      // Get cases count
      const { count: casesCount } = await supabase
        .from("cases")
        .select("*", { count: "exact", head: true })
        .eq("client_id", client.id);

      // Get documents count using the helper function
      let documentsCount = 0;
      try {
        documentsCount = await getClientDocumentCount(client.id);
      } catch (error) {
        console.error(
          `Error getting document count for client ${client.id}:`,
          error
        );
      }

      return {
        ...client,
        cases_count: casesCount || 0,
        documents_count: documentsCount || 0,
      };
    })
  );

  return clientsWithCounts;
}

/**
 * Get clients wanting lawyer review
 */
export async function getClientsWantingReview(country?: string) {
  let query = supabase
    .from("client_profiles")
    .select("*")
    .eq("wants_lawyer_review", true)
    .is("assigned_lawyer_id", null);

  if (country) {
    query = query.eq("country", country);
  }

  const { data, error } = await query.order("created_at", { ascending: true });

  if (error) throw error;
  return (data || []).map((c) => withProfiles(c));
}

/**
 * Increment document count
 */
export async function incrementDocumentCount(userId: string) {
  const current = await getClientProfile(userId);
  if (!current) throw new Error("Client profile not found");

  const { data, error } = await supabase
    .from("client_profiles")
    .update({
      document_count: (current.document_count || 0) + 1,
    })
    .eq("id", userId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Add uploaded document metadata
 */
export async function addUploadedDocument(
  userId: string,
  document: {
    filename: string;
    size: number;
    type: string;
    url: string;
    uploaded_at: string;
  }
) {
  const current = await getClientProfile(userId);
  if (!current) throw new Error("Client profile not found");

  type UploadedDoc = {
    filename: string;
    size: number;
    type: string;
    url: string;
    uploaded_at: string;
  };
  const documents = (current.uploaded_documents as UploadedDoc[] | null) ?? [];
  documents.push(document);

  const { data, error } = await supabase
    .from("client_profiles")
    .update({
      uploaded_documents: documents,
      document_count: documents.length,
    })
    .eq("id", userId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Create a new client manually (by lawyer).
 * Solo insert en client_profiles - sin auth, sin profiles, sin password.
 */
export async function createClient(
  lawyerId: string,
  clientData: {
    full_name: string;
    email: string;
    country: string;
    phone?: string;
    client_type?: "individual" | "business" | "nonprofit";
    company_name?: string;
    address?: string;
    legal_help_type?: string;
  }
) {
  const { data, error } = await supabase
    .from("client_profiles")
    .insert({
      id: crypto.randomUUID(),
      full_name: clientData.full_name,
      email: clientData.email,
      country: clientData.country || "RD",
      phone: clientData.phone ?? null,
      address: clientData.address ?? null,
      legal_help_type: clientData.legal_help_type ?? null,
      client_type: clientData.client_type ?? "individual",
      company_name: clientData.company_name ?? null,
      assigned_lawyer_id: lawyerId,
      lawyer_connection_date: new Date().toISOString(),
      status: "active",
    })
    .select()
    .single();

  if (error) throw error;
  if (!data) throw new Error("Failed to create client");

  return {
    ...data,
    profiles: {
      id: data.id,
      email: data.email,
      full_name: data.full_name,
      country: data.country,
      profile_image_url: null,
    },
  };
}

/**
 * Update client status
 */
export async function updateClientStatus(
  clientId: string,
  status: "active" | "inactive" | "deleted"
) {
  const { data, error } = await supabase
    .from("client_profiles")
    .update({ status, updated_at: new Date().toISOString() })
    .eq("id", clientId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Soft delete a client
 */
export async function softDeleteClient(clientId: string) {
  return updateClientStatus(clientId, "deleted");
}

/**
 * Get clients by lawyer with filters
 */
export async function getClientsByLawyerWithFilters(
  lawyerId: string,
  filters?: {
    status?: "active" | "inactive" | "deleted" | "all";
    client_type?: string;
    country?: string;
    search?: string;
  }
) {
  let query = supabase
    .from("client_profiles")
    .select("*")
    .eq("assigned_lawyer_id", lawyerId);

  // Apply status filter
  if (filters?.status && filters.status !== "all") {
    query = query.eq("status", filters.status);
  }

  // Apply client type filter
  if (filters?.client_type) {
    query = query.eq("client_type", filters.client_type);
  }

  // Apply country filter
  if (filters?.country) {
    query = query.eq("country", filters.country);
  }

  const { data, error } = await query.order("lawyer_connection_date", {
    ascending: false,
  });

  if (error) throw error;

  const normalized = (data || []).map((c) => withProfiles(c));

  type ClientWithProfiles = { id: string; profiles: { full_name: string; email: string } };
  // Get counts for all clients in parallel
  const clientsWithCounts = await Promise.all(
    normalized.map(async (client: ClientWithProfiles) => {
      // Get cases count
      const { count: casesCount } = await supabase
        .from("cases")
        .select("*", { count: "exact", head: true })
        .eq("client_id", client.id);

      // Get documents count using the helper function
      let documentsCount = 0;
      try {
        documentsCount = await getClientDocumentCount(client.id);
      } catch (error) {
        console.error(
          `Error getting document count for client ${client.id}:`,
          error
        );
      }

      return {
        ...client,
        cases_count: casesCount || 0,
        documents_count: documentsCount || 0,
      };
    })
  );

  // Apply search filter on client side
  if (filters?.search && clientsWithCounts) {
    const searchLower = filters.search.toLowerCase();
    return clientsWithCounts.filter((client: ClientWithProfiles) => {
      const fullName = client.profiles.full_name.toLowerCase();
      const email = client.profiles.email.toLowerCase();
      return fullName.includes(searchLower) || email.includes(searchLower);
    });
  }

  return clientsWithCounts;
}

/**
 * Get client statistics for a lawyer
 */
export async function getClientStatsForLawyer(lawyerId: string) {
  const { data, error } = await supabase
    .from("client_profiles")
    .select("status, client_type")
    .eq("assigned_lawyer_id", lawyerId);

  if (error) throw error;

  const stats = {
    total: data.length,
    active: 0,
    inactive: 0,
    deleted: 0,
    individual: 0,
    business: 0,
    nonprofit: 0,
  };

  data.forEach((client: { status: string | null; client_type: string | null }) => {
    // Count by status
    if (client.status === "active") stats.active++;
    else if (client.status === "inactive") stats.inactive++;
    else if (client.status === "deleted") stats.deleted++;

    // Count by type
    if (client.client_type === "individual") stats.individual++;
    else if (client.client_type === "business") stats.business++;
    else if (client.client_type === "nonprofit") stats.nonprofit++;
  });

  return stats;
}

/**
 * Get comprehensive statistics for a client
 * Returns: total cases, active cases, pending consultations, total documents, documents this month, assigned lawyer
 */
export async function getClientStats(clientId: string) {
  // Get total cases count
  const { count: totalCases, error: casesError } = await supabase
    .from("cases")
    .select("*", { count: "exact", head: true })
    .eq("client_id", clientId);

  if (casesError) throw casesError;

  // Get active cases count
  const { count: activeCases, error: activeCasesError } = await supabase
    .from("cases")
    .select("*", { count: "exact", head: true })
    .eq("client_id", clientId)
    .eq("status", "active");

  if (activeCasesError) throw activeCasesError;

  // Get pending consultations count
  const { count: pendingConsultations, error: consultationsError } =
    await supabase
      .from("consultations")
      .select("*", { count: "exact", head: true })
      .eq("client_id", clientId)
      .eq("status", "pending");

  if (consultationsError) throw consultationsError;

  // Get total documents count (from client_documents)
  const { count: totalDocuments, error: documentsError } = await supabase
    .from("client_documents")
    .select("*", { count: "exact", head: true })
    .eq("client_id", clientId);

  if (documentsError) throw documentsError;

  // Get documents this month
  const now = new Date();
  const startOfMonth = new Date(
    now.getFullYear(),
    now.getMonth(),
    1
  ).toISOString();
  const { count: documentsThisMonth, error: documentsMonthError } =
    await supabase
      .from("client_documents")
      .select("*", { count: "exact", head: true })
      .eq("client_id", clientId)
      .gte("assigned_at", startOfMonth);

  if (documentsMonthError) throw documentsMonthError;

  // Get assigned lawyer info
  const { data: clientProfile, error: clientError } = await supabase
    .from("client_profiles")
    .select(
      `
      assigned_lawyer_id,
      lawyer:lawyer_profiles!assigned_lawyer_id(
        id,
        profiles(
          full_name,
          email,
          profile_image_url
        )
      )
    `
    )
    .eq("id", clientId)
    .single();

  if (clientError) throw clientError;

  return {
    totalCases: totalCases || 0,
    activeCases: activeCases || 0,
    pendingConsultations: pendingConsultations || 0,
    totalDocuments: totalDocuments || 0,
    documentsThisMonth: documentsThisMonth || 0,
    assignedLawyer: clientProfile?.assigned_lawyer_id
      ? (() => {
          const lawyer = clientProfile.lawyer as {
            profiles?: {
              full_name?: string;
              email?: string;
              profile_image_url?: string;
            };
          } | null;
          return {
            id: clientProfile.assigned_lawyer_id,
            name: lawyer?.profiles?.full_name ?? null,
            email: lawyer?.profiles?.email ?? null,
            profileImageUrl: lawyer?.profiles?.profile_image_url ?? null,
          };
        })()
      : null,
  };
}
